import type { TableEditResult } from '../schemas'
import Checkbox from '@/components/checkbox/Checkbox'
import { Select } from '@/components/select/Select'
import { columnIcons } from './column-icons'
import { useEffect, useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import FloatLabelFormTextArea from '@/matter/components/matter-dialogs/FloatLabelFormTextArea'
import { Button } from '@/components/button/Button'
import { ColumnKind, type TableColumn, type TableEdit } from '../schemas'

const fieldClass = 'flex flex-col gap-1 rounded border border-brand-neutral-400 px-3 py-2 focus-within:ring-1 focus-within:ring-brand-500'

export default function ColumnForm({
  column,
  documentBased,
  saving,
  loading = false,
  onSave,
  onClose,
  onDirtyChange,
  error,
}: {
  error?: string | null
  column?: TableColumn
  documentBased: boolean
  saving: boolean
  loading?: boolean
  onSave: (edit: TableEdit) => Promise<TableEditResult>
  onClose: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const formId = useId()
  const { register, watch } = useForm({ defaultValues: { columnName: column?.name ?? '', question: column?.question ?? '' } })
  const { columnName, question } = watch()
  const [manualEntry, setManualEntry] = useState(column?.manual_entry ?? false)
  const [kind, setKind] = useState(column?.kind ?? ColumnKind.Text)
  const dirty =
    columnName !== (column?.name ?? '') ||
    question !== (column?.question ?? '') ||
    manualEntry !== (column?.manual_entry ?? false) ||
    kind !== (column?.kind ?? ColumnKind.Text)
  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  const isAnalysisColumn = documentBased && !manualEntry
  const labelRequired = Boolean(column) || !isAnalysisColumn
  const FormatIcon = columnIcons[kind]
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (event) => {
        event.preventDefault()
        const name = columnName.trim()
        let edit: TableEdit
        if (column) {
          edit = {
            action: 'edit-column',
            column_id: column.id,
            name,
            question: question.trim(),
            ...(documentBased ? { manual_entry: manualEntry } : {}),
            ...(kind !== column.kind ? { kind } : {}),
          }
        } else if (isAnalysisColumn && !name) {
          edit = { action: 'add-analysis-column', question: question.trim(), kind }
        } else if (isAnalysisColumn) {
          edit = { action: 'add-column', name, question: question.trim(), kind }
        } else {
          edit = { action: 'add-column', name, kind, ...(documentBased ? { manual_entry: true } : {}) }
        }
        if ((await onSave(edit)).ok) onClose()
      }}
    >
      <label className={`${fieldClass} !flex-row items-center gap-3`}>
        <FormatIcon className="h-5 w-5 shrink-0 text-brand-neutral-950" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="text-xs text-brand-neutral-500">Format *</span>
          <Select
            ariaLabel="Format"
            required
            selectedItemId={kind}
            onSelect={(value) => setKind(value as ColumnKind)}
            disabled={saving}
            triggerClassName="h-auto border-0 bg-transparent p-0 text-brand-neutral-950 focus-visible:ring-0"
            items={[
              { id: ColumnKind.Text, label: 'Text' },
              { id: ColumnKind.Number, label: 'Number' },
              { id: ColumnKind.Date, label: 'Date' },
            ]}
          />
        </span>
      </label>
      {column && kind !== column.kind && question.trim() === column.question && (
        <p className="text-xs text-brand-neutral-600">
          Changing only format preserves answers and citations without rerunning analysis. Filters that depend on the previous format will be removed.
        </p>
      )}
      {documentBased && (
        <label className="flex items-center gap-2 text-sm text-brand-neutral-800">
          <Checkbox checked={manualEntry} onCheckedChange={(checked) => setManualEntry(checked === true)} disabled={saving} />
          Manual entry
        </label>
      )}
      {isAnalysisColumn && (
        <FloatLabelFormTextArea
          id={`${formId}-question`}
          label="Analysis question"
          required={!column}
          registration={register('question')}
          disabled={saving}
          textareaProps={{ 'aria-label': 'Analysis question', required: !column }}
        />
      )}
      <InputFieldFloatingLabel
        id={`${formId}-label`}
        label="Column label"
        required={labelRequired}
        registration={register('columnName')}
        disabled={saving}
        inputProps={{ 'aria-label': 'Column label', required: labelRequired }}
      />
      {documentBased && manualEntry && <p className="text-xs text-brand-neutral-600">Enter values yourself. Document analysis will skip this column.</p>}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-brand-neutral-500">
          <span className="text-red-700">*</span> Required fields
        </span>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outlined" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            disabled={(labelRequired && !columnName.trim()) || (isAnalysisColumn && !column && !question.trim()) || saving || loading}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </form>
  )
}
